import React, { useState, useEffect, useCallback, useRef } from 'react';

/**
 * 평가셋 라벨링 — 우측 drawer 형식 (인라인 작업)
 * - 현재 선택 파일 자동 prefill (Donut/PaddleOCR/딥러닝 결과 출발점)
 * - 양식 보고 잘못된 필드만 수정 → GT 저장
 * - 5건+ 누적 시 자동 또는 수동 재학습 trigger
 */

const FIELDS_B = [
  { key: 'vin', label: '차대번호 (VIN, 17자)' },
  { key: 'plate_no', label: '자동차등록번호' },
  { key: 'car_name', label: '차명 (영문)' },
  { key: 'year', label: '모델연도 (4자리)' },
  { key: 'mileage_km', label: '주행거리 (km)' },
  { key: 'owner_name', label: '소유자명' },
  { key: 'owner_id_no', label: '주민/법인번호 (양식 그대로)' },
  { key: 'owner_address', label: '사용본거지/주소' },
  { key: 'displacement_cc', label: '배기량 (cc)' },
  { key: 'vehicle_weight_kg', label: '총중량 (kg)' },
  { key: 'canceled_date', label: '말소등록일 (YYYY-MM-DD)' },
  { key: 'registration_date', label: '최초등록일 (YYYY-MM-DD)' },
];

const FIELDS_I = [
  { key: 'vin', label: '차대번호 (VIN)' },
  { key: 'plate_no', label: '자동차등록번호' },
  { key: 'car_name', label: '차명 (영문)' },
  { key: 'year', label: '모델연도' },
  { key: 'owner_name', label: '소유자명' },
  { key: 'owner_id_no', label: '주민/법인번호' },
  { key: 'owner_address', label: '사용본거지' },
  { key: 'displacement_cc', label: '배기량' },
  { key: 'vehicle_weight_kg', label: '총중량' },
  { key: 'length_cm', label: '길이 (mm)' },
  { key: 'width_cm', label: '너비 (mm)' },
  { key: 'height_cm', label: '높이 (mm)' },
  { key: 'registration_date', label: '최초등록일' },
];

function mapExtractionToFields(src) {
  if (!src || typeof src !== 'object') return {};
  const m = {};
  const copy = (k, ...sources) => {
    for (const s of sources) {
      const v = src[s];
      if (v != null && String(v).trim() !== '') { m[k] = String(v).trim(); return; }
    }
  };
  copy('vin', 'vin', 'VIN');
  copy('plate_no', 'plate_no', 'plateNumber', 'vehicleRegistrationNumber');
  copy('car_name', 'car_name', 'vehicleName');
  copy('year', 'year', 'modelYear');
  copy('mileage_km', 'mileage_km', 'mileage');
  copy('owner_name', 'owner_name', 'ownerName');
  copy('owner_id_no', 'owner_id_no', 'ownerIdNumber');
  copy('owner_address', 'owner_address', 'ownerAddress');
  copy('displacement_cc', 'displacement_cc', 'displacement');
  copy('vehicle_weight_kg', 'vehicle_weight_kg', 'grossWeight');
  copy('length_cm', 'length_cm', 'length_mm', 'length');
  copy('width_cm', 'width_cm', 'width_mm', 'width');
  copy('height_cm', 'height_cm', 'height_mm', 'height');
  copy('canceled_date', 'canceled_date', '말소등록일');
  copy('registration_date', 'registration_date');
  return m;
}

export default function EvalLabelingModal({
  open, onClose,
  currentFile = null, currentFileName = '',
  paddleResult = null, donutResult = null, deepResult = null,
}) {
  const [docType, setDocType] = useState('B');
  const [id, setId] = useState('');
  const [gt, setGt] = useState({});
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [labels, setLabels] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState(null);
  const [source, setSource] = useState('current');
  const [retraining, setRetraining] = useState(false);
  const [retrainMsg, setRetrainMsg] = useState(null);
  const fileInputRef = useRef(null);
  const lastSyncedNameRef = useRef(null);

  const fields = docType === 'B' ? FIELDS_B : FIELDS_I;

  const loadLabels = useCallback(async () => {
    try {
      const r = await fetch('/api/eval/labels');
      const d = await r.json();
      setLabels(d.labels || []);
    } catch {}
  }, []);

  useEffect(() => { if (open) loadLabels(); }, [open, loadLabels]);

  const handleFile = useCallback((f) => {
    setFile(f);
    if (!f) { setPreviewUrl(null); return; }
    const url = URL.createObjectURL(f);
    setPreviewUrl(url);
  }, []);

  // 모달 열릴 때 + 선택 파일 변경 시 자동 동기화
  useEffect(() => {
    if (!open) { lastSyncedNameRef.current = null; return; }
    if (currentFile && currentFile.name !== lastSyncedNameRef.current) {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      handleFile(currentFile);
      const baseName = (currentFileName || currentFile.name).replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9_-]/g, '_');
      setId(`eval_${docType}_${baseName}_${Date.now().toString().slice(-6)}`);
      lastSyncedNameRef.current = currentFile.name;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentFile, currentFileName, docType]);

  // 소스 변경 시 폼 자동 채움
  useEffect(() => {
    if (!open) return;
    let src = null;
    if (source === 'current') {
      if (donutResult) src = donutResult;
      else if (paddleResult) src = paddleResult;
      else if (deepResult) src = deepResult;
    } else if (source === 'paddle') src = paddleResult;
    else if (source === 'donut') src = donutResult;
    else if (source === 'deep') src = deepResult;
    if (src) {
      const mapped = mapExtractionToFields(src);
      setGt(mapped);
      if (mapped.canceled_date) setDocType('B');
      else if (mapped.length_cm || mapped.width_cm) setDocType('I');
    } else if (source === 'empty') {
      setGt({});
    }
  }, [open, source, paddleResult, donutResult, deepResult]);

  const handleSave = async () => {
    setError(null);
    if (!id) { setError('ID 입력 필요'); return; }
    if (!file) { setError('파일 필요'); return; }
    setSaving(true);
    try {
      const cleanGt = {};
      Object.entries(gt).forEach(([k, v]) => {
        if (v != null && String(v).trim() !== '') cleanGt[k] = String(v).trim();
      });
      const fd = new FormData();
      fd.append('file', file);
      fd.append('id', id);
      fd.append('doc_type', docType);
      fd.append('gt', JSON.stringify(cleanGt));
      fd.append('file_name', file.name);
      fd.append('notes', notes);
      const r = await fetch('/api/eval/labels', { method: 'POST', body: fd });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt(null), 3000);
      loadLabels();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (delId) => {
    if (!window.confirm(`삭제: ${delId}?`)) return;
    try {
      await fetch(`/api/eval/labels/${encodeURIComponent(delId)}`, { method: 'DELETE' });
      loadLabels();
    } catch {}
  };

  const handleRetrain = async () => {
    if (labels.length < 10) {
      setError(`v4 풀 재학습 최소 10건 필요 (현재 ${labels.length}건). 30건+ 권장.`);
      return;
    }
    if (!window.confirm(
      `v4 풀 재학습을 시작합니다.\n\n` +
      `· 사용자 라벨: ${labels.length}건\n` +
      `· v2 manifest: 14,603건 (자동 통합)\n` +
      `· corrections: 자동 적용\n` +
      `· base: v2 final (forgetting 위험 0)\n` +
      `· 시간: 약 3시간\n\n` +
      `학습 중 inference 자동 종료, 끝나면 v4 모델로 자동 교체됩니다.\n\n계속?`
    )) return;
    setRetraining(true); setError(null); setRetrainMsg(null);
    try {
      const r = await fetch('/api/eval/retrain-v4', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setRetrainMsg(d.message || 'v4 학습 시작');
    } catch (e) { setError('v4 재학습 실패: ' + e.message); }
    finally { setRetraining(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed right-0 top-0 bottom-0 z-40 w-[480px] bg-white shadow-2xl border-l flex flex-col" onClick={(e) => e.stopPropagation()}>
      <div className="px-4 py-2 border-b flex items-center justify-between bg-gray-50">
        <div className="flex items-center gap-2">
          <span className="text-xl">🏷️</span>
          <h2 className="text-sm font-semibold">GT 라벨링 (양식 그대로)</h2>
        </div>
        <div className="flex gap-2 items-center">
          <span className="text-xs text-gray-500">저장: {labels.length}건</span>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-900 text-lg">✕</button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3">
        <div className="mb-2 px-2 py-1.5 bg-gray-50 border rounded text-xs">
          <div className="flex items-center justify-between gap-2">
            <div className="truncate"><b>📄 {file?.name || '파일 없음'}</b></div>
            <input ref={fileInputRef} type="file" accept="image/*,application/pdf"
              onChange={(e) => handleFile(e.target.files?.[0])} className="hidden" id="eval-file-input" />
            <label htmlFor="eval-file-input" className="text-blue-600 hover:underline cursor-pointer text-[10px]">변경</label>
          </div>
        </div>

        <div className="space-y-2 mb-3">
          <div className="flex gap-2 items-center">
            <label className="text-xs w-20">ID</label>
            <input value={id} onChange={(e) => setId(e.target.value)}
              className="flex-1 px-2 py-1 border rounded text-xs font-mono" />
          </div>
          <div className="flex gap-2 items-center">
            <label className="text-xs w-20">Doc type</label>
            <div className="flex gap-2">
              {[['B', '말소'], ['I', '등록증']].map(([k, lbl]) => (
                <label key={k} className="flex items-center gap-1 text-xs cursor-pointer">
                  <input type="radio" name="doctype" value={k} checked={docType === k} onChange={() => setDocType(k)} />
                  <span>{lbl}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded p-2 mb-2 text-[11px]">
          <div className="flex items-center gap-2 flex-wrap">
            <b className="text-blue-900">출발점:</b>
            {[['current', '🤖 자동', true], ['donut', '🟣 Donut', !!donutResult], ['paddle', '🟢 Paddle', !!paddleResult], ['deep', '🔵 딥러닝', !!deepResult], ['empty', '빈 폼', true]].map(([k, lbl, en]) => (
              <label key={k} className={`flex items-center gap-1 ${en ? 'cursor-pointer' : 'opacity-30 cursor-not-allowed'}`}>
                <input type="radio" name="source" value={k} checked={source === k} disabled={!en} onChange={() => setSource(k)} />
                <span>{lbl}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded p-2 mb-2 text-[11px] text-amber-800">
          ⚠️ <b>양식에 보이는 값만</b> 입력. 안 보이면 빈칸.
        </div>

        <div className="space-y-1.5">
          {fields.map(({ key, label }) => (
            <div key={key} className="flex items-center gap-2">
              <label className="text-[11px] text-gray-700 w-44 flex-shrink-0">{label}</label>
              <input value={gt[key] || ''} onChange={(e) => setGt(prev => ({ ...prev, [key]: e.target.value }))}
                className="flex-1 px-2 py-1 border rounded text-xs" placeholder="(none)" />
            </div>
          ))}
          <div className="flex items-start gap-2 mt-3">
            <label className="text-[11px] w-44 flex-shrink-0 pt-1">메모</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              className="flex-1 px-2 py-1 border rounded text-xs" />
          </div>
        </div>

        {error && <div className="mt-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">{error}</div>}
        {savedAt && <div className="mt-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2">✅ 저장됨</div>}
      </div>

      <div className="border-t bg-white px-3 py-2 max-h-28 overflow-y-auto">
        <div className="text-[11px] font-medium mb-1">최근 라벨 ({labels.length}건)</div>
        {labels.length === 0 ? <div className="text-[10px] text-gray-400">없음</div> : (
          <div className="space-y-0.5 text-[10px]">
            {labels.slice(-5).reverse().map(l => (
              <div key={l.id} className="flex items-center justify-between bg-gray-50 px-2 py-0.5 rounded">
                <span className="truncate flex-1">
                  <b className={l.doc_type === 'B' ? 'text-emerald-700' : 'text-blue-700'}>{l.doc_type}</b>{' '}
                  <span className="font-mono">{l.id.slice(-20)}</span>{' '}
                  <span className="text-gray-500">({Object.keys(l.gt || {}).length}필드)</span>
                </span>
                <button onClick={() => handleDelete(l.id)} className="text-red-500 hover:text-red-700 ml-1">✕</button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="px-3 py-2 border-t bg-gray-50 flex flex-col gap-1.5">
        <button onClick={handleSave} disabled={saving || !file || !id}
          className="w-full px-4 py-2 text-sm bg-blue-600 text-white font-semibold rounded hover:bg-blue-700 disabled:opacity-50">
          {saving ? '저장 중...' : '💾 GT로 저장'}
        </button>
        <div className="flex gap-1.5">
          <button onClick={handleRetrain} disabled={retraining || labels.length < 10}
            title={labels.length < 10 ? `최소 10건 필요 (현재 ${labels.length}건). 30건+ 권장.` : 'v2 manifest 14,603건 + 사용자 라벨 통합 → v4 처음부터 학습 (forgetting 0)'}
            className="flex-1 px-2 py-1.5 text-[11px] bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-40">
            {retraining ? '시작 중...' : `🚀 v4 풀 재학습 (${labels.length}건+14k)`}
          </button>
          <button onClick={onClose} className="px-3 py-1.5 text-[11px] hover:bg-gray-200 rounded">닫기</button>
        </div>
        {retrainMsg && <div className="text-[10px] text-emerald-700">{retrainMsg}</div>}
      </div>
    </div>
  );
}
